import { retry } from 'rxjs/operators';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { BehaviorSubject, Observable, Subject, Subscription, throwError, timer } from 'rxjs';

import { WS_STATUS, WsChannel } from './ws.interfaces';
import { camelDataKeys, snakeDataKey } from '../helpers/data-helper';


const MAX_RECONNECT_DELAY_MS = 30000;
const JITTER_RATIO = 0.3;

/**
 * What a channel needs from whoever created it. Grouped rather than passed positionally: the
 * two numbers are indistinguishable at a call site, and `log`/`onError` belong to the factory,
 * not to the socket.
 */
export interface WsChannelOptions {
  /** Base of the exponential backoff, not a fixed interval. */
  reconnectInterval: number;
  reconnectAttempts: number;
  log: (...args: unknown[]) => void;
  onError: (error: unknown) => void;
}

/**
 * One live socket: its frames, its status, and its reconnect policy.
 *
 * Not exported from the barrel — `WebSocketService` is the only thing that may create one, so
 * that every channel in the app shares one url, one logger and one error reporter.
 */
export class WsChannelRef<TIn, TOut> implements WsChannel<TIn, TOut> {
  public readonly messages$: Observable<TIn>;
  public readonly status$: Observable<WS_STATUS>;

  private readonly _messages = new Subject<TIn>();
  private readonly _status = new BehaviorSubject<WS_STATUS>(WS_STATUS.CONNECTING);
  // rxjs models a socket with a single type parameter for both directions, so the two are
  // only rejoined here; `WsChannel` keeps them apart for everyone above.
  private readonly _socket$: WebSocketSubject<TIn | TOut>;
  private readonly _subscription: Subscription;
  private _isClosedByClient = false;
  private _attempt = 0;

  constructor(private _url: string,
              private _options: WsChannelOptions) {

    this.messages$ = this._messages.asObservable();
    this.status$ = this._status.asObservable();

    this._socket$ = webSocket<TIn | TOut>({
      url: this._url,
      serializer: (msg) => JSON.stringify(snakeDataKey(msg)),
      deserializer: ({ data }) => camelDataKeys(JSON.parse(data)),
      openObserver: {
        next: () => {
          this._options.log('Established', this._url);
          // Resets both the delay and the attempt budget, which `_backoff$` counts off the same
          // field: a long-lived session that reconnects many times must not run out of attempts
          // for a drop it has not had yet.
          this._attempt = 0;
          this._status.next(WS_STATUS.OPEN);
        }
      },
      // Deliberately does not touch the status: rxjs keeps calling this for the socket's whole
      // life, including for the close event that follows the error that ended the retry chain.
      // Reporting RECONNECTING from here overwrote that final FAILED and left the channel
      // claiming forever that it was still trying. `_backoff$` is the only thing that knows a
      // retry is actually scheduled, so it is the only thing that says so.
      closeObserver: {
        next: () => this._options.log('Stopped', this._url)
      }
    });

    this._subscription = this._socket$
      .pipe(retry({ delay: (error) => this._backoff$(error) }))
      .subscribe({
        next: (frame) => this._messages.next(frame as TIn),
        error: (error) => {
          this._options.onError(error);
          this._status.next(WS_STATUS.FAILED);
        },
        // A clean server-side close completes the stream without exhausting the retry budget,
        // which `retry` does not resubscribe to. Reported as FAILED because the effect on the
        // user is the same: no more messages will arrive.
        complete: () => {
          if (!this._isClosedByClient) {
            this._status.next(WS_STATUS.FAILED);
          }
        }
      });
  }

  public get isOpen(): boolean {
    return this._status.value === WS_STATUS.OPEN;
  }

  public send(payload: TOut): boolean {
    if (!this.isOpen) {
      return false;
    }

    this._socket$.next(payload);

    return true;
  }

  /** Deliberate teardown, so it reports IDLE rather than FAILED: nothing went wrong. */
  public close() {
    this._options.log('Closing', this._url);
    this._isClosedByClient = true;
    this._status.next(WS_STATUS.IDLE);
    this._subscription.unsubscribe();
    this._socket$.unsubscribe();
    this._messages.complete();
    this._status.complete();
  }

  /**
   * Schedules the next attempt with exponential backoff and jitter, so a mass reconnect does
   * not stampede the server — or gives up by rethrowing, which ends the retry chain in FAILED.
   *
   * The budget is counted here rather than through `retry`'s own `count`, because that counter
   * is only reset by an incoming value (`resetOnSuccess`) and a chat socket can sit open and
   * silent for hours. `_attempt` is reset when the socket opens, which is what "attempts since
   * we last had a connection" actually means.
   */
  private _backoff$(error: unknown): Observable<number> {
    if (this._attempt >= this._options.reconnectAttempts) {
      return throwError(() => error);
    }

    this._attempt += 1;
    this._status.next(WS_STATUS.RECONNECTING);

    const backoffMs = Math.min(
      this._options.reconnectInterval * 2 ** (this._attempt - 1),
      MAX_RECONNECT_DELAY_MS,
    );

    return timer(backoffMs + Math.random() * backoffMs * JITTER_RATIO);
  }
}
